import { Flags } from "@oclif/core"
import { action } from "@oclif/core/ux"
import { SentryTraced } from "@sentry/nestjs"
import { exec as execCallback } from "node:child_process"
import * as fs from "node:fs/promises"
import path, { resolve } from "node:path"
import { promisify } from "node:util"
import { fileURLToPath } from "node:url"
import { z, ZodType } from "zod"
import fsx from "fs-extra"
import crypto from "node:crypto"

import { commonFlags, SentryCommand } from "../types/index.js"

import { DTOSandboxTemplateBuildCreateInput, SupernovaApiClient } from "@supernova-studio/client"
import { sleep } from "../utils/common.js"
import { tmpdir } from "node:os"
import { discoverAndUpdatePackageJson, discoverFilesForTemplates, TemplateInfo } from "../utils/discover.js"
import { spawnAndWait } from "../utils/spawn-and-wait.js"
import { buildTemplateUploadDockerfile, validateTemplateUploadNpmrcFile } from "../utils/template-upload-npmrc.js"
import { uploadFileToUrl } from "../utils/upload-file-to-signed-url.js"
import { fileExists, TemplatePreset, validateTemplates } from "../utils/validate-templates.js"

const exec = promisify(execCallback)

const TemplateUploadConfig = z.object({})

type TemplateUploadConfig = z.infer<typeof TemplateUploadConfig>

export default class TemplateUpload extends SentryCommand<TemplateUploadConfig> {
  static override args = {}
  static override description = "Upload component container template to Supernova"
  static override examples = ["<%= config.bin %> <%= command.id %> TemplateUpload "]
  static override hidden: boolean = false
  static override flags = {
    ...commonFlags,
    workspaceId: Flags.string({ char: "w", description: "Workspace ID to upload the template to", required: false }),
    designSystemId: Flags.string({
      char: "d",
      description: "Design system ID to upload the template to",
      required: false,
    }),
    force: Flags.boolean({
      char: "f",
      description:
        "Allows overwriting already published version of this template if it exists. This flag has no effect on new versions.",
      required: false,
    }),
    npmToken: Flags.string({
      description: "Allows passing NPM token as a Docker secret. The token will be available as NPM_TOKEN env variable",
      required: false,
    }),
    discover: Flags.boolean({
      description: "Run template and pattern discovery before upload to update package.json",
      required: false,
    }),
    dockerImageUri: Flags.string({
      description:
        "Set this flag to upload image to a custom Docker registry, omit to use Supernova Docker registry. " +
        "The registry needs to be accessible by Supernova, " +
        "and authentication must be provided using 'dockerUser' and 'dockerPassword' flags",
      required: false,
    }),
    dockerUser: Flags.string({
      description:
        "Username for accessing custom Docker registry, to be used in combination with 'dockerImageUri'. Supernova will pass it to 'docker login'.",
      required: false,
    }),
    dockerPassword: Flags.string({
      description:
        "Password for accessing custom Docker registry, to be used in combination with 'dockerImageUri'. Supernova will pass it to 'docker login'. " +
        "Set this to '-' to accept password from stdin.",
      required: false,
      allowStdin: true,
    }),
    uploadAsTar: Flags.boolean({
      description:
        "Export the built Docker image as a tar file and upload it through the files API instead of pushing it to a Docker registry.",
      required: false,
      default: false,
    }),
    imageTar: Flags.string({
      description:
        "Path to a Docker image exported as tar. When used, CLI will skip internal Docker build and upload the image instead. " +
        "The image must comply with basic Supernova requirements.",
      required: false,
    }),
    debug: Flags.boolean({
      description: "Preserve shell directory and App.tsx on template build failures for debugging",
      required: false,
      hidden: true,
    }),
  }

  get commandId(): string {
    return TemplateUpload.id
  }

  get configSchema(): ZodType<TemplateUploadConfig> {
    return TemplateUploadConfig
  }

  @SentryTraced()
  public async run(): Promise<void> {
    const { flags } = await this.parse()
    this.validateFlags(flags)

    const apiClient = await this.apiClient()

    // Update package.json with discovered names/descriptions if requested
    if (flags.discover) {
      action.start("🔍 Running template and pattern discovery")
      try {
        const { templates, patterns } = await discoverAndUpdatePackageJson(process.cwd())
        action.stop(`found ${Object.keys(templates).length} templates, ${Object.keys(patterns).length} patterns`)
      } catch (error) {
        action.stop("failed")
        if (error instanceof Error) this.error(`Discovery failed: ${error.message}`)
        else throw error
      }
    }

    // Check if this is discover-only mode
    const isDiscoverOnly = flags.discover && !flags.workspaceId && !flags.designSystemId

    if (isDiscoverOnly) {
      this.log("✅ Discovery completed. Use --workspaceId and --designSystemId to upload templates.")
      return
    }

    // Validate required flags for upload
    if (!flags.workspaceId) {
      this.error("Missing required flag workspaceId")
    }

    if (!flags.designSystemId) {
      this.error("Missing required flag designSystemId")
    }

    // Read package json
    let pkg
    try {
      pkg = await readPackageJson()
    } catch (error) {
      if (error instanceof Error) this.error(`Failed to read or parse package.json: ${error.message}`)
      else throw error
    }

    if (pkg.supernova?.privateDependencies) {
      this.log(`The following packages will be linked as private dependencies: ${pkg.supernova.privateDependencies}`)

      for (const dependency of pkg.supernova.privateDependencies) {
        if (dependency.endsWith("/*")) continue
        if (!pkg.dependencies[dependency]) {
          this.error(`Private dependency ${dependency} is not listed in 'dependencies'`)
        }
      }

      if (!(await fileExists(path.join(process.cwd(), ".npmrc")))) {
        this.error(
          `CLI needs private NPM registry access to be able to bundle private dependencies.\n` +
            `Please provide .npmrc file in the root directory and include neccessary access tokens.`,
        )
      }
    } else {
      this.warn(`package.json doesn't contain 'supernova.privateDependencies' declaration.`)
      this.warn(`Dependencies coming from private registries will fail`)
    }

    try {
      await validateTemplateUploadNpmrcFile(path.join(process.cwd(), ".npmrc"), flags.npmToken)
    } catch (error) {
      if (error instanceof Error) this.error(error.message)
      else throw error
    }

    let templatesWithThumbnailUrls: DTOSandboxTemplateBuildCreateInput["templates"] | undefined

    if (pkg.supernova?.templates) {
      // Always enrich templates from package.json with discovered files/thumbnails info on the fly
      const templates = await discoverFilesForTemplates(pkg.supernova.templates, process.cwd())

      await validateTemplates(templates, this, flags.debug)
      templatesWithThumbnailUrls = await this.uploadThumbnailsAndBuildTemplates(apiClient, flags.workspaceId, templates)
    }

    const hasDesignModePlugin = await detectDesignModePlugin()

    // Request build
    const buildData = await apiClient.sandboxes.builds.start({
      workspaceId: flags.workspaceId,
      designSystemId: flags.designSystemId,
      name: pkg.name,
      version: pkg.version,
      isExistingVersionUpdateAllowed: flags.force ?? false,
      templates: templatesWithThumbnailUrls,
      hasDesignModePlugin,
      dockerImageUri: flags.dockerImageUri,
      ...(flags.dockerUser &&
        flags.dockerPassword && {
          customAuth: { username: flags.dockerUser, password: flags.dockerPassword },
        }),
    })

    // Build image

    await this.validateDockerDaemon()
    const buildDir = await this.createBuildDir()

    try {
      // Build Docker image unless provided from outside
      if (!flags.imageTar) {
        await this.prepareBuildFolder(buildDir)
        await this.buildDockerImage(buildDir, buildData.build.dockerImageUri, flags.npmToken)
      }

      // Upload the Docker image to Supernova (either as a file or via `docker push`)
      const { imageFileId } = await this.uploadDockerImage(apiClient, buildDir, buildData.build.dockerImageUri, flags)

      // Trigger E2B build on the server
      await this.remoteTemplateBuild(apiClient, buildData.build.id, imageFileId)

      this.log(`✅ Template has been successfully uploaded`)
    } finally {
      await this.deleteBuildDir(buildDir)
    }
  }

  // Unfortunately right now the way flag parsing is written it produces `any` type, therefore no proper type here
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private validateFlags(flags: any) {
    if (flags.uploadAsTar) {
      if (flags.dockerImageUri) this.error(`Flag '--dockerImageUri' can't be used with '--uploadAsTar'.`)
      if (flags.dockerUser) this.error(`Flag '--dockerUser' can't be used with '--uploadAsTar'.`)
      if (flags.dockerPassword) this.error(`Flag '--dockerPassword' can't be used with '--uploadAsTar'.`)
    }

    if (flags.dockerImageUri) {
      if (!flags.dockerUser) this.error(`Flag '--dockerUser' is required when '--dockerImageUri' is set.`)
      if (!flags.dockerPassword) this.error(`Flag '--dockerPassword' is required when '--dockerImageUri' is set.`)
    }

    if (!flags.dockerImageUri) {
      if (flags.dockerUser) this.error(`Flag '--dockerUser' can't be used without '--dockerImageUri'.`)
      if (flags.dockerPassword) this.error(`Flag '--dockerPassword' can't be used without '--dockerImageUri'.`)
    }
  }

  private async validateDockerDaemon() {
    await exec("docker info").catch(() => {
      this.error(`Docker is not available, please start docker daemon and try again`)
    })
  }

  private createBuildDir(): Promise<string> {
    return fs.mkdtemp(path.join(tmpdir(), "supernova-template-bundle-"))
  }

  private async deleteBuildDir(buildDir: string) {
    await fs.rm(buildDir, { recursive: true, force: true })
  }

  private async prepareBuildFolder(buildDir: string) {
    // Move working directory to temp folder from where it will be built
    await fsx.copy(process.cwd(), buildDir, {
      filter(src) {
        return !src.includes("node_modules/") && !src.includes(".git/") && !src.includes(".out/")
      },
    })

    // Inject Supernova scripts
    const cliSrcPath = path.resolve(path.dirname(fileURLToPath(import.meta.url)), "..")

    await fsx.copy(path.join(cliSrcPath, "docker-scripts"), path.join(buildDir, "docker-scripts"))
  }

  private async buildDockerImage(buildDir: string, imageUri: string, npmToken: string | undefined) {
    this.log("🔨 Building docker image")

    const dockerfile = buildTemplateUploadDockerfile(npmToken)

    const dockerBuildArgs = ["build", "-t", imageUri, "--pull", "--platform", "linux/amd64"]
    if (npmToken) dockerBuildArgs.push("--secret", "id=NPM_TOKEN")
    dockerBuildArgs.push("-f", "-", ".")

    await spawnAndWait("docker", dockerBuildArgs, {
      cwd: buildDir,
      env: npmToken ? { ...process.env, NPM_TOKEN: npmToken } : process.env,
      stdinData: dockerfile,
    })

    this.log("🔨 Docker image has been built")
  }

  private async uploadDockerImage(
    apiClient: SupernovaApiClient,
    buildDir: string,
    imageUri: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    flags: any,
  ): Promise<{ imageFileId?: string }> {
    if (flags.uploadAsTar) {
      return {
        imageFileId: await this.uploadDockerImageAsTarFromDocker(apiClient, imageUri, flags.workspaceId),
      }
    }

    if (flags.imageTar) {
      const fullImageTarPath = resolve(flags.imageTar)
      console.log(fullImageTarPath)
      return {
        imageFileId: await this.uploadDockerImageAsTarFromFileSystem(apiClient, fullImageTarPath, flags.workspaceId),
      }
    }

    await this.pushDockerImage(buildDir, imageUri, flags)
    return {}
  }

  private async uploadDockerImageAsTarFromDocker(
    apiClient: SupernovaApiClient,
    imageUri: string,
    workspaceId: string,
  ): Promise<string> {
    const tarFilePath = path.join(tmpdir(), `${crypto.randomUUID()}.tar`)

    try {
      action.start("📦 Exporting docker image as tar")
      await exec(`docker save -o "${tarFilePath}" ${imageUri}`)
      action.stop("done")

      return await this.uploadDockerImageAsTarFromFileSystem(apiClient, tarFilePath, workspaceId)
    } finally {
      await fs.rm(tarFilePath, { force: true })
    }
  }

  private async uploadDockerImageAsTarFromFileSystem(
    apiClient: SupernovaApiClient,
    tarFilePath: string,
    workspaceId: string,
  ): Promise<string> {
    if (!(await fileExists(tarFilePath))) {
      this.error(`File ${tarFilePath} doesn't exist.`)
    }

    const size = await getFileSize(tarFilePath)
    const checksum = await calculateFileChecksum(tarFilePath)

    this.log(`📦 Docker tar file size: ${(size / 1000 / 1000).toFixed(2)}MB`)

    action.start(`⬆️  Uploading docker image tar`)

    const uploadResponse = await apiClient.files.upload({
      ownerType: "Workspace",
      workspaceId,
      files: [{ name: path.basename(tarFilePath), size, checksum }],
    })

    await uploadFilesToUrls(uploadResponse.uploadUrls, uploadResponse.files, [
      { name: path.basename(tarFilePath), size, originalPath: tarFilePath },
    ])

    await apiClient.files.finalizeUpload({
      ownerType: "Workspace",
      workspaceId,
      fileIds: uploadResponse.uploadUrls.map(file => file.fileId),
    })

    action.stop("done")

    return uploadResponse.files[0].id
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async pushDockerImage(buildDir: string, imageUri: string, flags: any) {
    const dockerHost = new URL(`https://${imageUri}`).hostname

    let user: string | undefined
    let password: string | undefined
    let dockerLabel: string

    if (flags.dockerImageUri) {
      // Custom user-managed registry
      user = flags.dockerUser
      password = flags.dockerPassword
      dockerLabel = dockerHost
    } else {
      // Supernova registry
      const { accessToken } = (await this.apiClient()).config
      user = "cli"
      password = accessToken
      dockerLabel = "Supernova"
    }

    if (!user || !password) {
      this.error(`Docker registry credentials are missing`)
    }

    await spawnAndWait("docker", ["login", dockerHost, "-u", user, "--password-stdin"], {
      stdinData: password,
    })

    this.log(`⬆️  Uploading docker image to ${dockerLabel}`)
    await spawnAndWait("docker", ["push", imageUri], { cwd: buildDir })
    this.log("⬆️  Docker image has been uploaded")
  }

  private async remoteTemplateBuild(client: SupernovaApiClient, buildId: string, imageFileId?: string) {
    action.start("📦 Creating template with the image")

    // Trigger remote build phase
    await client.sandboxes.builds.finalize(buildId, { imageFileId })

    // Poll every 2 seconds for 15 minutes
    const pollIntervalMs = 2000
    const timeoutMs = 15 * 60 * 1000

    const startTime = Date.now()

    let build
    do {
      await sleep(pollIntervalMs)
      build = (await client.sandboxes.builds.get(buildId)).build
    } while (build.state === "Building" && Date.now() - startTime < timeoutMs)

    // Verify build state
    if (build.state === "Success") {
      action.stop("done")
    } else {
      action.stop("failed")
      this.error(`Template creation failed`)
    }
  }

  private async uploadThumbnailsAndBuildTemplates(
    apiClient: SupernovaApiClient,
    workspaceId: string,
    templates: { [key: string]: TemplateInfo },
  ): Promise<DTOSandboxTemplateBuildCreateInput["templates"]> {
    // Validate template names are unique
    const templateNames = Object.values(templates).map(t => t.name)
    const duplicateNames = templateNames.filter((name, index) => templateNames.indexOf(name) !== index)
    if (duplicateNames.length > 0) {
      throw new Error(
        `Duplicate template names found: ${[...new Set(duplicateNames)].join(", ")}. Each template must have a unique name.`,
      )
    }

    const templateCount = Object.keys(templates).length
    action.start(`📸 Processing ${templateCount} template(s)`)

    const thumbnailFiles: Array<{
      name: string
      size: number
      checksum: string
      originalPath: string
      templateId: string
    }> = []

    // Validate thumbnail files
    for (const [templateId, template] of Object.entries(templates)) {
      if (!template.thumbnail) continue

      const validation = await validateThumbnailFile(templateId, template.thumbnail, this)
      if (!validation) continue

      try {
        const checksum = await calculateFileChecksum(validation.fullPath)
        thumbnailFiles.push({
          name: validation.name,
          size: validation.size,
          checksum,
          originalPath: validation.fullPath,
          templateId,
        })
      } catch (error) {
        this.warn(
          `Failed to calculate checksum for ${validation.fullPath}: ${error instanceof Error ? error.message : String(error)}`,
        )
        continue
      }
    }

    let thumbnailUrlMap: { [templateId: string]: string } = {}

    if (thumbnailFiles.length > 0) {
      try {
        // Request upload URLs from API
        const uploadResponse = await apiClient.files.upload({
          ownerType: "Workspace",
          workspaceId,
          files: thumbnailFiles.map(({ name, size, checksum }) => ({ name, size, checksum })),
        })

        // Upload files to signed URLs
        await uploadFilesToUrls(uploadResponse.uploadUrls, uploadResponse.files, thumbnailFiles)

        await apiClient.files.finalizeUpload({
          ownerType: "Workspace",
          workspaceId,
          fileIds: uploadResponse.uploadUrls.map(f => f.fileId),
        })

        // Map template IDs to their uploaded thumbnail URLs
        thumbnailUrlMap = buildTemplateUrlMap(thumbnailFiles, uploadResponse.files)
      } catch (error) {
        this.warn(`Failed to upload thumbnails: ${error instanceof Error ? error.message : String(error)}`)
        this.warn("Continuing without uploaded thumbnails...")
        // Don't error out completely, just continue without uploaded thumbnails
      }
    }

    const message =
      thumbnailFiles.length > 0 ? `uploaded ${thumbnailFiles.length} thumbnail(s)` : "no thumbnails to upload"
    action.stop(message)

    const templatesWithUrls = Object.entries(templates).map(([id, template]) => ({
      id,
      name: template.name,
      description: template.description,
      thumbnailUrl: thumbnailUrlMap[id],
      files: template.files,
    }))

    return templatesWithUrls
  }
}

type PackageJson = {
  name: string
  version: string
  dependencies: Record<string, string>
  supernova:
    | {
        privateDependencies: string[] | undefined
        templates?: {
          [key: string]: TemplatePreset
        }
      }
    | undefined
}

async function readPackageJson(): Promise<PackageJson> {
  const pkgPath = path.join(process.cwd(), "package.json")

  if (!(await fileExists(pkgPath))) throw new Error(`package.json file was not found in the current directory`)

  const raw = await fs.readFile(pkgPath, "utf8")
  const pkg = JSON.parse(raw)

  if (typeof pkg !== "object" || pkg === null) throw new Error(`Error parsing package.json: not a json`)

  if (typeof pkg.name !== "string") throw new Error(`Error parsing package.json: 'name' must be defined`)
  if (typeof pkg.version !== "string") throw new Error(`Error parsing package.json: 'version' must be defined`)
  if (typeof pkg.dependencies !== "object" || pkg.dependencies === null)
    throw new Error(`Error parsing package.json: 'dependencies' must be defined`)

  if (pkg.supernova?.privateDependencies) {
    const privateDependencies = pkg.supernova?.privateDependencies
    if (!Array.isArray(privateDependencies)) throw new TypeError(`supernova.privateDependencies must be an array`)

    for (const [i, d] of privateDependencies.entries()) {
      if (typeof d !== "string") {
        throw new TypeError(`supernova.privateDependencies[${i}] must be a string`)
      }

      if (!isValidPrivateDependencyEntry(d)) {
        throw new TypeError(
          `supernova.privateDependencies[${i}] must be a package name or scope wildcard like "@example/*"`,
        )
      }
    }
  }

  return pkg
}

function isValidPrivateDependencyEntry(entry: string): boolean {
  if (!entry || entry.includes(" ")) return false
  if (entry.endsWith("/*")) {
    const scope = entry.slice(0, -2)
    return scope.startsWith("@") && !scope.includes("/")
  }

  return true
}

async function validateThumbnailFile(
  templateId: string,
  thumbnailPath: string,
  logger: { warn: (msg: string) => void },
): Promise<{ name: string; size: number; fullPath: string } | undefined> {
  const allowedExtensions = [".png", ".jpg", ".jpeg", ".svg", ".webp"]
  const maxFileSize = 10 * 1024 * 1024 // 10 MB in bytes

  const fullPath = path.resolve(thumbnailPath)
  const name = path.basename(thumbnailPath)

  if (!(await fileExists(fullPath))) {
    logger.warn(`Thumbnail file not found for template ${templateId}: ${thumbnailPath}`)
    return undefined
  }

  const extension = path.extname(thumbnailPath).toLowerCase()
  if (!allowedExtensions.includes(extension)) {
    logger.warn(
      `Thumbnail file ${name} for template ${templateId} has unsupported format. Allowed formats: ${allowedExtensions.join(", ")}`,
    )
    return undefined
  }

  const size = await getFileSize(fullPath)
  if (size > maxFileSize) {
    logger.warn(
      `Thumbnail file ${name} for template ${templateId} is too large (${(size / 1024 / 1024).toFixed(2)}MB). Maximum size is 10MB.`,
    )
    return undefined
  }

  return { name, size, fullPath }
}

async function uploadFilesToUrls(
  uploadUrls: Array<{ fileId: string; uploadUrl: string }>,
  fileResponses: Array<{ id: string; name: string; size: number }>,
  localFiles: Array<{ name: string; size: number; originalPath: string }>,
): Promise<void> {
  const uploadTasks = uploadUrls.map(async uploadUrl => {
    const fileResponse = fileResponses.find(f => f.id === uploadUrl.fileId)
    const localFile = localFiles.find(f => f.name === fileResponse?.name && f.size === fileResponse?.size)

    if (localFile) {
      await uploadFileToUrl({
        uploadUrl: uploadUrl.uploadUrl,
        filePath: localFile.originalPath,
      })
    }
  })

  await Promise.all(uploadTasks)
}

function buildTemplateUrlMap(
  thumbnailFiles: Array<{ templateId: string; checksum: string }>,
  fileResponses: Array<{ url: string; deduplicationKey: string }>,
): { [templateId: string]: string } {
  const thumbnailUrlMap: { [templateId: string]: string } = {}

  for (const thumbnailFile of thumbnailFiles) {
    const fileResponse = fileResponses.find(f => f.deduplicationKey === thumbnailFile.checksum)
    if (fileResponse) {
      thumbnailUrlMap[thumbnailFile.templateId] = fileResponse.url
    }
  }

  return thumbnailUrlMap
}

async function calculateFileChecksum(filePath: string): Promise<string> {
  const fileBuffer = await fs.readFile(filePath)
  const hashBuffer = await crypto.subtle.digest("SHA-256", fileBuffer)
  return Buffer.from(hashBuffer).toString("hex")
}

async function getFileSize(filePath: string): Promise<number> {
  const stats = await fs.stat(filePath)
  return stats.size
}

async function detectDesignModePlugin(): Promise<true | undefined> {
  const viteConfigPath = path.join(process.cwd(), "vite.config.ts")

  try {
    const content = await fs.readFile(viteConfigPath, "utf8")
    // Simple string check — supernovaDesignPlugin won't appear in vite.config.ts by accident
    if (content.includes("supernovaDesignPlugin")) {
      return true
    }
  } catch {
    // vite.config.ts not found or unreadable — ignore
  }

  return undefined
}
